import {AfterViewInit, Component, Inject, NgZone, ViewChild} from '@angular/core';
import {TorchQueryFiltersService} from '../../../../../../@backend/modules/@filters/torch-query-filters.service';
import {TorchQueryFilter} from '../../../../../../@backend/modules/@filters/torch-query-filter.model';
import {BehaviorSubject, merge, Subject} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {filter, map, pairwise, startWith, switchMap, take, throttleTime} from 'rxjs/operators';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {QueryFilterAction} from '../../interfaces/query-filter-action.interface';
import {blub, inOutAnimations, rowRemove} from './animations';
import {QueryFilterDialogData} from '../../query-filter-dialog.config';
import {QueryFilterDialogType} from '../../query-filter-dialog.type';
import {QueryFilterEditComponent} from './dialog-components/query-filter-edit/query-filter-edit.component';
import {FileSystemFileEntry, NgxFileDropEntry} from 'ngx-file-drop';
import {AlertService} from '../../../../services/alert.service';

@Component({
  selector: 'app-query-filter-dialog',
  templateUrl: './query-filter-dialog.component.html',
  styleUrls: ['./query-filter-dialog.component.scss'],
  animations: [rowRemove, blub, inOutAnimations],
})
export class QueryFilterDialogComponent implements AfterViewInit {

  @ViewChild('scroller') scroller: CdkVirtualScrollViewport;
  @ViewChild(QueryFilterEditComponent) filterEditor: QueryFilterEditComponent;

  totalFilters: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  queryFilters: TorchQueryFilter[] = [];
  searchChanged: Subject<string> = new Subject<string>();
  searchValue: string;
  currentOffset = 0;
  limit = 25;
  noItems = false;
  isEditing: TorchQueryFilter = null;
  isLoading = true;

  hideActions = false;
  showList = true;
  dialogTitle: string;

  private allFilters: TorchQueryFilter[] = [];

  constructor(private queryFilterService: TorchQueryFiltersService,
              private alertService: AlertService,
              public dialogRef: MatDialogRef<QueryFilterDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: QueryFilterDialogData,
              private ngZone: NgZone) {

    this.configureDialog();

    merge(this.searchChanged).pipe(
      startWith({}),
      switchMap(() => this.fetchMoreItems(true))
    ).subscribe(response => {
      this.queryFilters = response.data;
      this.noItems = response.total === 0;
      this.isLoading = false;

      if (this.totalFilters.value === null) {
        this.totalFilters.next(response.total);
      }
    });
  }

  ngAfterViewInit() {
    this.scroller.elementScrolled().pipe(
      map(() => this.scroller.measureScrollOffset('bottom')),
      pairwise(),
      filter(([y1, y2]) => (y2 < y1 && y2 < 140)),
      throttleTime(200)
    ).subscribe(() => {
      this.ngZone.run(() => {
        this.fetchMoreItems();
      });
    });
  }

  fetchMoreItems(initial: boolean = false) {
    if (initial) {
      this.currentOffset = 0;
      return this.queryFilterService.getFilters(this.currentOffset, this.limit, this.searchValue);
    } else {
      this.currentOffset = this.currentOffset + this.limit;
      this.queryFilterService.getFilters(this.currentOffset, this.limit, this.searchValue).subscribe(response => {
        this.queryFilters = [...this.queryFilters, ...response.data];
        this.noItems = response.total === 0;
      });
    }
  }

  filterIcon(queryFilter: TorchQueryFilter): string {
    if (!!queryFilter.metadata && !!queryFilter.metadata.icon && queryFilter.metadata.icon.length) {
      return queryFilter.metadata.icon;
    }

    return 'rule';
  }

  searchUpdated() {
    if (!!this.searchValue && !!this.searchValue.length) {
      this.searchChanged.next(this.searchValue);
    } else {
      this.searchChanged.next(null);
      this.searchValue = null;
    }
  }

  clearSearch() {
    this.searchValue = '';
    this.searchUpdated();
  }

  handleAction(action: QueryFilterAction) {
    if (action.type === 'DELETE') {
      this.deleteFilter(action.queryFilter);
    } else if (action.type === 'EDIT') {
      this.isEditing = action.queryFilter;
      this.showList = false;
      this.handleEditing();
    }
  }

  updateFilter(queryFilter: TorchQueryFilter | null) {
    this.isEditing = null;
    this.queryFilters = this.allFilters;
    this.dialogTitle = this.data.title;
    this.showList = this.data.type !== QueryFilterDialogType.EDIT_EXISTING;

    if (!!queryFilter) {
      if (this.data.type !== QueryFilterDialogType.SAVE_NEW) {
        this.queryFilterService.updateFilter(queryFilter).pipe(
          take(1),
        ).subscribe(() => {
          this.searchChanged.next('');
        });
      }
    }

    if (this.data.type === QueryFilterDialogType.EDIT_EXISTING || this.data.type === QueryFilterDialogType.SAVE_NEW) {
      this.dialogRef.close(queryFilter);
    }
  }

  deleteFilter(queryFilter: TorchQueryFilter) {
    this.queryFilterService.deleteFilter(queryFilter).subscribe(() => {
      this.searchChanged.next('');
    });
  }

  filterSelected(queryFilter: TorchQueryFilter) {
    if (!!this.isEditing) {
      return queryFilter === this.isEditing;
    }

    return false;
  }

  handleRowClick(queryFilter: TorchQueryFilter) {
    if (this.data.type === QueryFilterDialogType.PICK_FILTER) {
      this.dialogRef.close(queryFilter);
    } else if (this.data.type === QueryFilterDialogType.SAVE_NEW) {
      this.filterEditor.setDisplayName(queryFilter.displayName);
    }
  }

  handleFilterDropped(droppedFilters: NgxFileDropEntry[]) {
    const droppedFilter = droppedFilters[0];

    if (droppedFilter.fileEntry.isFile) {
      const fileEntry = droppedFilter.fileEntry as FileSystemFileEntry;
      fileEntry.file(file => {
        const fileReader = new FileReader();
        fileReader.readAsText(file, 'UTF-8');

        fileReader.onload = () => {
          this.isLoading = true;

          try {
            const rawFilter: string = fileReader.result as string;
            const parsedFilter: TorchQueryFilter = JSON.parse(atob(rawFilter));

            if (this.queryFilterService.validateFilter(parsedFilter)) {
              this.queryFilterService.createFilter(parsedFilter).subscribe(() => {
                this.searchChanged.next('');
              });
            } else {
              console.error('Filter was not valid', parsedFilter);

              this.alertService.setCurrentAlert({
                message: 'TORCH Query Filter was not valid',
                type: 'danger'
              }, 500);

              this.isLoading = false;
            }
          } catch (e) {
            console.error(e);
            this.isLoading = false;
          }
        };
      });
    }
  }

  get editorType() {
    if (this.data.type === QueryFilterDialogType.SAVE_NEW) {
      return 'NEW';
    }

    return 'EDIT';
  }

  get showCursor(): boolean {
    return this.data.type === QueryFilterDialogType.SAVE_NEW || this.data.type === QueryFilterDialogType.PICK_FILTER;
  }

  private handleEditing() {
    this.allFilters = this.queryFilters;

    this.queryFilters = this.queryFilters.filter(f => f.id === this.isEditing.id);
    this.scroller.scrollToIndex(0, 'smooth');
    this.dialogTitle = `Editing ${this.isEditing.displayName}`;
  }

  private configureDialog() {
    const type = this.data.type;

    this.dialogTitle = this.data.title;

    if (
      (type === QueryFilterDialogType.EDIT_EXISTING || type === QueryFilterDialogType.SAVE_NEW) && !!this.data.filter
    ) {
      this.isEditing = this.data.filter;
      this.showList = type === QueryFilterDialogType.SAVE_NEW;
      this.hideActions = true;
    }

    if (type === QueryFilterDialogType.PICK_FILTER) {
      this.hideActions = true;
    }
  }
}
